import React, { useEffect, useId, useState } from "react"
import cx from "classnames"
import { VisuallyHidden } from "../visually-hidden"
import { PageSecondaryHeaderCenterContent } from "./page-secondary-header-center-content"
import { PageSecondaryHeaderMenu } from "./page-secondary-header-menu"
import type { PageSecondaryHeaderMenuItem } from "./page-secondary-header-menu"
import type { PageSecondaryHeaderStepProps } from "./page-secondary-header-step"

// =============================================================================
// Props
// =============================================================================

export type PageSecondaryHeaderStepsProps = {
  /** Page header steps */
  children?: React.ReactNode
  className?: string
}

// =============================================================================
// Styles
// =============================================================================

const pageSecondaryHeaderStepsContainerStyles = [
  "py-1",
  // Display menu until `@6xl/page-header`
  // Then display the expanded row of steps
  "hidden @6xl/page-header:flex items-center justify-center"
]

const pageSecondaryHeaderStepsStyles = ["flex items-center"]

// =============================================================================
// Component
// =============================================================================

/**
 * Secondary page header steps.
 *
 * Displays the current step in a workflow. Shown above `@6xl/page-header`.
 */
export function PageSecondaryHeaderSteps({
  children,
  className
}: PageSecondaryHeaderStepsProps) {
  const accessibleNameId = useId()

  const stepChildren = React.Children.toArray(children).filter(
    React.isValidElement
  ) as React.ReactElement<PageSecondaryHeaderStepProps>[]

  // The current step is driven by each step's `isCurrent` prop (the source of
  // truth), but steps are often plain buttons with no reactive source. Mirror
  // `isCurrent` in local state so selecting a step from the collapsed menu
  // updates the trigger immediately, then re-sync if `isCurrent` changes.
  const currentStepIndex = stepChildren.findIndex(
    child => child.props.isCurrent
  )
  const [activeIndex, setActiveIndex] = useState(currentStepIndex)

  useEffect(() => {
    setActiveIndex(currentStepIndex)
  }, [currentStepIndex])

  const items: PageSecondaryHeaderMenuItem[] = stepChildren.map(
    (child, index) => {
      const { children: label, href, render, isCurrent, ...rest } = child.props
      const renderElement = React.isValidElement(render) ? render : undefined
      // The menu item injects the label as children at runtime, so the anchor
      // is built without children (via `createElement` to keep it so). The
      // step's own `onClick` rides along on the rendered element, so it must
      // not be attached to `Menu.Item` too (it would fire twice).
      const itemRender =
        renderElement ??
        (href !== undefined
          ? React.createElement("a", { href, ...rest })
          : undefined)
      const stepOnClick = rest.onClick as
        | ((event: React.MouseEvent) => void)
        | undefined

      return {
        key: href ?? String(index),
        label,
        isActive: index === activeIndex,
        render: itemRender,
        onClick: (event: React.MouseEvent) => {
          setActiveIndex(index)
          // Button steps have no rendered element to carry the handler.
          if (!itemRender) stepOnClick?.(event)
        }
      }
    }
  )

  return (
    <PageSecondaryHeaderCenterContent>
      <nav
        className={cx(pageSecondaryHeaderStepsContainerStyles)}
        aria-labelledby={accessibleNameId}
        data-slot="secondary-steps"
      >
        <VisuallyHidden id={accessibleNameId}>Steps</VisuallyHidden>
        <ol className={cx(pageSecondaryHeaderStepsStyles, className)}>
          {children}
        </ol>
      </nav>
      <PageSecondaryHeaderMenu
        accessibleName="Steps"
        items={items}
        dataSlot="secondary-steps-menu"
      />
    </PageSecondaryHeaderCenterContent>
  )
}
